const MAX_FILE_SIZE = 50 * 1024 * 1024
const SUPPORTED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.bmp', '.tiff']

export interface UploadFailure {
  file: File
  message: string
  retryable: boolean
}

export interface ImageFileValidationResult {
  validFiles: File[]
  failures: UploadFailure[]
}

export interface UploadProgress {
  total: number
  completed: number
  succeeded: number
  failed: number
}

export interface UploadResult {
  successful: File[]
  failed: UploadFailure[]
}

export function validateImageFiles(files: Iterable<File>): ImageFileValidationResult {
  const selectedFiles = Array.from(files)
  const validFiles: File[] = []
  const failures: UploadFailure[] = []

  selectedFiles.forEach(file => {
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
    if (!SUPPORTED_EXTENSIONS.includes(extension)) {
      failures.push({
        file,
        message: 'Unsupported file type. Use .png, .jpg, .jpeg, .bmp, or .tiff.',
        retryable: false,
      })
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      failures.push({
        file,
        message: 'File exceeds the 50 MiB size limit.',
        retryable: false,
      })
      return
    }

    validFiles.push(file)
  })

  return { validFiles, failures }
}

// Client errors the server may answer differently on a later attempt.
const RETRYABLE_CLIENT_STATUSES = [408, 429]

function getErrorResponse(error: unknown): Record<string, unknown> | null {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null
  ) {
    return error.response as Record<string, unknown>
  }

  return null
}

function getUploadErrorMessage(error: unknown): string {
  const response = getErrorResponse(error)
  const data = response?.data
  if (
    typeof data === 'object' &&
    data !== null &&
    'detail' in data &&
    typeof data.detail === 'string'
  ) {
    return data.detail
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  return 'Failed to upload image'
}

function isRetryableUploadError(error: unknown): boolean {
  const status = getErrorResponse(error)?.status

  // No status means the request never got an answer — a network failure worth retrying.
  if (typeof status !== 'number') {
    return true
  }

  if (status >= 400 && status < 500) {
    return RETRYABLE_CLIENT_STATUSES.includes(status)
  }

  return true
}

export async function uploadFilesWithConcurrency(
  files: readonly File[],
  upload: (file: File) => Promise<unknown>,
  concurrency: number,
  onProgress: (progress: UploadProgress) => void
): Promise<UploadResult> {
  if (!Number.isInteger(concurrency) || concurrency < 1) {
    throw new Error('Concurrency must be a positive integer')
  }

  const outcomes: Array<UploadFailure | null | undefined> = new Array(files.length)
  let nextIndex = 0
  let completed = 0
  let succeeded = 0
  let failed = 0

  const worker = async () => {
    while (nextIndex < files.length) {
      const index = nextIndex
      nextIndex += 1
      const file = files[index]

      try {
        await upload(file)
        outcomes[index] = null
        succeeded += 1
      } catch (error) {
        outcomes[index] = {
          file,
          message: getUploadErrorMessage(error),
          retryable: isRetryableUploadError(error),
        }
        failed += 1
      }

      completed += 1
      onProgress({ total: files.length, completed, succeeded, failed })
    }
  }

  const workerCount = Math.min(concurrency, files.length)
  await Promise.all(Array.from({ length: workerCount }, () => worker()))

  return {
    successful: files.filter((_, index) => outcomes[index] === null),
    failed: outcomes.filter(
      (outcome): outcome is UploadFailure => outcome !== null && outcome !== undefined
    ),
  }
}
